import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import JSZip from "jszip";
import { useWizard } from "../../context/WizardContext";
import { useAuth } from "../../context/AuthContext";
import Modal from "./Modal";
import { resolveReferencePhotoUrl } from "../../utils/presignedImageUrl";
import LogoLoader from "../common/LogoLoader";
import { API_BASE_URL, API_ORIGIN_BASE_URL } from "../../config/apiBaseUrl";
import AdminPageNavigator from "../AdminPageNavigator";

interface TrainingPrompt {
  tags: string;
  originalName: string;
  prompt: string;
  filename: string;
}

interface ImageData {
  filename: string;
  originalName: string;
  path: string;
  tags: string;
  preview?: string;
  /** Set when images are stored on R2 (public URL). */
  publicUrl?: string;
  storageKey?: string;
}

const PhotoTraining: React.FC = () => {
  const { 
    sessionId, 
    images,
    isLoading,
    error,
    clearError
  } = useWizard();

  const { user, getAuthHeader, token } = useAuth();
  const isAdmin = user?.isAdmin === true;
  
  const navigate = useNavigate();

  const [trainingPrompts, setTrainingPrompts] = useState<TrainingPrompt[]>([]);
  const [personDescriptions, setPersonDescriptions] = useState<{ [key: number]: string }>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState<boolean>(true);
  const [localError, setLocalError] = useState<string | null>(null);
  const [uploadedImages, setUploadedImages] = useState<ImageData[]>([]);
  const [clearedTextareas, setClearedTextareas] = useState<Set<number>>(new Set());
  const [resolvedPhotoUrls, setResolvedPhotoUrls] = useState<Record<number, string>>({});
  const [expandedPromptIndexes, setExpandedPromptIndexes] = useState<Set<number>>(new Set());
  const [copiedPromptIndexes, setCopiedPromptIndexes] = useState<Set<number>>(new Set());
  const [isDownloadingAllPictures, setIsDownloadingAllPictures] = useState(false);

  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: 'info' | 'success' | 'warning' | 'error' | 'confirm';
    title: string;
    message: string;
    onConfirm?: () => void;
    confirmText?: string;
    icon?: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  });

  useEffect(() => {
    if (!sessionId) {
      navigate("/create/language");
      return;
    }

    loadTrainingData();
  }, [sessionId, navigate, getAuthHeader]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        uploadedImages.map(async (img, index) => {
          const u = await resolveReferencePhotoUrl(img, token);
          return [index, u || ""] as const;
        })
      );
      if (cancelled) return;
      const next: Record<number, string> = {};
      entries.forEach(([i, u]) => {
        if (u) next[i] = u;
      });
      setResolvedPhotoUrls(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [uploadedImages, token]);

  const loadTrainingData = async () => {
    setIsLoadingPrompts(true);
    clearError();
    setLocalError(null);

    try {
      // Load story data to get uploaded images
      const storyResponse = await fetch(`${API_BASE_URL}/stories/${sessionId}`, {
        headers: { ...getAuthHeader() },
      });
      if (!storyResponse.ok) {
        throw new Error('Failed to load story data');
      }
      const storyData = await storyResponse.json();
      const story = storyData.data;

      if (!story.images || story.images.length === 0) {
        showModal('error', 'No Images Found', 'Please upload images first before training.', undefined, 'OK', '❌');
        navigate("/create/photos-upload");
        return;
      }

      setUploadedImages(story.images);
      
      // Debug: Log image data to see what we're working with
      console.log(
        '📸 Loaded images:',
        story.images.map((img: any) => ({
          filename: img.filename,
          originalName: img.originalName,
          storageKey: img.storageKey ?? null,
        }))
      );

      // Get training prompts (development mode)
      const trainingResponse = await fetch(`${API_BASE_URL}/stories/${sessionId}/train-images`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
      });

      const trainingData = await trainingResponse.json();

      if (!trainingResponse.ok) {
        throw new Error(trainingData.message || 'Failed to load training prompts');
      }

      // Check if training is already done or just completed (production mode auto-trains)
      // In production mode, train-images endpoint automatically trains, so if we have learnedPeople, training is done
      const isProductionMode = story.generationMode === 'production';
      if (isProductionMode && trainingData.data.learnedPeople && trainingData.data.learnedPeople.length > 0) {
        // For admins, allow revisiting this page (don't auto-skip to Image Studio)
        if (!isAdmin) {
          console.log('✅ Training completed (production mode). Navigating to image studio...');
          navigate("/create/image-studio");
          return;
        }
      }

      // Check if training was already done previously
      if (trainingData.data.alreadyTrained && trainingData.data.learnedPeople && trainingData.data.learnedPeople.length > 0) {
        // For admins, allow revisiting this page (don't auto-skip to Image Studio)
        if (!isAdmin) {
          console.log('✅ Training already completed. Navigating to image studio...');
          navigate("/create/image-studio");
          return;
        }
      }

      // Check if we already have learned people (saved descriptions)
      if (trainingData.data.learnedPeople && trainingData.data.learnedPeople.length > 0) {
        // Load existing descriptions
        const descriptions: { [key: number]: string } = {};
        trainingData.data.learnedPeople.forEach((person: any, index: number) => {
          descriptions[index] = person.personDescription || '';
        });
        setPersonDescriptions(descriptions);
      }

      // Get prompts (either from response or generate them)
      if (trainingData.data.prompts && trainingData.data.prompts.length > 0) {
        setTrainingPrompts(trainingData.data.prompts);
      } else if (trainingData.data.learnedPeople && trainingData.data.learnedPeople.length > 0) {
        // If we have learned people but no prompts, generate prompts from images
        const prompts: TrainingPrompt[] = story.images.map((img: ImageData, index: number) => {
          const person = trainingData.data.learnedPeople[index];
          return {
            tags: img.tags || person?.tags || '',
            originalName: img.originalName || person?.originalName || '',
            prompt: `Analyze this photo and describe the physical characteristics of the person/people in detail for future illustration consistency.

Photo tags: ${img.tags || 'No specific tags'}
Filename: ${img.originalName || 'Unknown'}

Please provide a detailed description including:
- Physical appearance (age, gender, height, build)
- Facial features (eye color, hair color and style, facial structure)
- Distinctive characteristics (glasses, beard, smile, etc.)
- Clothing style (if relevant for character consistency)

This description will be used to create consistent illustrations of this person in a children's storybook. Focus on features that would help an artist draw this person consistently across multiple illustrations.

Format your response as: "Person with [tags]: [detailed description]"`,
            filename: img.filename || img.originalName
          };
        });
        setTrainingPrompts(prompts);
      } else {
        // Generate prompts from images
        const prompts: TrainingPrompt[] = story.images.map((img: ImageData) => ({
          tags: img.tags || '',
          originalName: img.originalName || '',
          prompt: `Analyze this photo and describe the physical characteristics of the person/people in detail for future illustration consistency.

Photo tags: ${img.tags || 'No specific tags'}
Filename: ${img.originalName || 'Unknown'}

Please provide a detailed description including:
- Physical appearance (age, gender, height, build)
- Facial features (eye color, hair color and style, facial structure)
- Distinctive characteristics (glasses, beard, smile, etc.)
- Clothing style (if relevant for character consistency)

This description will be used to create consistent illustrations of this person in a children's storybook. Focus on features that would help an artist draw this person consistently across multiple illustrations.

Format your response as: "Person with [tags]: [detailed description]"`,
          filename: img.filename || img.originalName
        }));
        setTrainingPrompts(prompts);
      }
    } catch (err: any) {
      console.error('Failed to load training data:', err);
      setLocalError(err.message || 'Failed to load training data');
    } finally {
      setIsLoadingPrompts(false);
    }
  };

  const handleDescriptionChange = (index: number, description: string) => {
    setPersonDescriptions(prev => ({
      ...prev,
      [index]: description
    }));
  };

  const handleTextareaClick = (index: number) => {
    const currentValue = personDescriptions[index] || '';
    const defaultText = '[Development Mode] Use the prompt below to train with this image.';
    
    // Only clear if it's the default text and hasn't been cleared yet
    if (currentValue === defaultText && !clearedTextareas.has(index)) {
      setPersonDescriptions(prev => ({
        ...prev,
        [index]: ''
      }));
      setClearedTextareas(prev => new Set(prev).add(index));
    }
  };

  const togglePromptExpanded = (index: number) => {
    setExpandedPromptIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleDownloadTrainingImage = async (
    index: number,
    promptData: TrainingPrompt,
    image: ImageData | undefined,
    imageUrl: string
  ) => {
    const trimmed = (imageUrl || "").trim();
    if (!trimmed) {
      showModal("warning", "No image", "Preview is not available for this photo yet.", undefined, "OK", "⚠️");
      return;
    }
    const rawName =
      (promptData.originalName && String(promptData.originalName).trim()) ||
      (image?.originalName && String(image.originalName).trim()) ||
      (image?.filename && String(image.filename).trim()) ||
      `photo-${index + 1}`;
    const safeBase = rawName.replace(/[/\\?%*:|"<>]/g, "-").slice(0, 180) || `photo-${index + 1}`;
    const downloadName = /\.[a-zA-Z0-9]{2,8}$/.test(safeBase) ? safeBase : `${safeBase}.webp`;

    try {
      const blob = await fetchTrainingImageBlob(image, trimmed);
      if (!blob) throw new Error("Download failed");
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error("PhotoTraining download:", e);
      showModal(
        "error",
        "Download failed",
        "Could not download this image. If it opens in a new tab, save it from there.",
        undefined,
        "OK",
        "❌"
      );
    }
  };

  const handleSave = async () => {
    // Validate that all descriptions are filled
    const missingDescriptions = trainingPrompts.filter((_, index) => !personDescriptions[index]?.trim());
    if (missingDescriptions.length > 0) {
      showModal(
        'warning',
        'Missing Descriptions',
        `Please provide AI responses for all ${missingDescriptions.length} image(s) before saving.`,
        undefined,
        'OK',
        '⚠️'
      );
      return;
    }

    setIsSaving(true);
    clearError();
    setLocalError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/save-training`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({
          trainingResults: trainingPrompts.map((prompt, index) => ({
            tags: prompt.tags,
            originalName: prompt.originalName,
            personDescription: personDescriptions[index]?.trim() || ''
          }))
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to save training data');
      }

      console.log('✅ Training data saved successfully. Navigating to image studio...');
      
      // Navigate directly to image studio - the story should already exist
      // ImageStudio will handle the case if story doesn't exist
      navigate("/create/image-studio");
    } catch (err: any) {
      console.error('Failed to save training data:', err);
      setLocalError(err.message || 'Failed to save training data');
      showModal(
        'error',
        'Save Failed',
        err.message || 'Failed to save training data. Please try again.',
        undefined,
        'OK',
        '❌'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const getImageUrl = (image: ImageData): string => {
    if (image.preview) {
      return image.preview;
    }
    if (image.publicUrl && /^https?:\/\//i.test(image.publicUrl)) {
      return image.publicUrl;
    }
    if (image.storageKey) {
      return '';
    }

    // Construct URL to backend image (local uploads/ only; R2 uses presign via resolvedPhotoUrls)
    const baseUrl = API_ORIGIN_BASE_URL;
    
    // If path is already a URL, return it
    if (image.path && image.path.startsWith('http')) {
      return image.path;
    }
    
    // Extract filename from path (handle both absolute and relative paths)
    let filename = image.filename;
    if (image.path) {
      // If path contains 'uploads', extract the filename part
      const pathParts = image.path.split(/[/\\]/);
      const uploadsIndex = pathParts.findIndex(part => part === 'uploads');
      if (uploadsIndex >= 0 && uploadsIndex < pathParts.length - 1) {
        // Get everything after 'uploads'
        filename = pathParts.slice(uploadsIndex + 1).join('/');
      } else {
        // Just get the last part (filename)
        filename = pathParts[pathParts.length - 1];
      }
    }
    
    // Construct the URL: http://localhost:5000/uploads/filename
    return `${baseUrl}/uploads/${filename}`;
  };

  const fetchTrainingImageBlob = async (
    image: ImageData | undefined,
    imageUrl: string
  ): Promise<Blob | null> => {
    const trimmed = (imageUrl || "").trim();
    if (!trimmed) return null;
    try {
      if (image?.storageKey) {
        const res = await fetch(
          `${API_BASE_URL}/images/file?key=${encodeURIComponent(image.storageKey)}`,
          { headers: { ...getAuthHeader() } }
        );
        if (!res.ok) return null;
        return await res.blob();
      }
      const res = await fetch(trimmed, { mode: "cors" });
      if (!res.ok) return null;
      return await res.blob();
    } catch {
      return null;
    }
  };

  const handleDownloadAllStoryPictures = async () => {
    if (!isAdmin || trainingPrompts.length === 0) return;
    setIsDownloadingAllPictures(true);
    try {
      const zip = new JSZip();
      const folder = zip.folder("story-photos");
      if (!folder) throw new Error("Could not create archive folder.");

      let added = 0;
      for (let index = 0; index < trainingPrompts.length; index++) {
        const promptData = trainingPrompts[index];
        const image = uploadedImages[index];
        const contextImage = images[index];
        const imageUrl =
          resolvedPhotoUrls[index] ||
          contextImage?.preview ||
          (image ? getImageUrl(image) : "");
        const trimmed = (imageUrl || "").trim();
        if (!trimmed) continue;

        const blob = await fetchTrainingImageBlob(image, trimmed);
        if (!blob) continue;

        const rawName =
          (promptData.originalName && String(promptData.originalName).trim()) ||
          (image?.originalName && String(image.originalName).trim()) ||
          (image?.filename && String(image.filename).trim()) ||
          `photo-${index + 1}`;
        const safeBase = rawName.replace(/[/\\?%*:|"<>]/g, "-").slice(0, 120) || `photo-${index + 1}`;
        const hasExt = /\.[a-zA-Z0-9]{2,8}$/.test(safeBase);
        const entryName = `${String(index + 1).padStart(2, "0")}-${hasExt ? safeBase : `${safeBase}.webp`}`;
        folder.file(entryName, blob);
        added += 1;
      }

      if (added === 0) {
        showModal(
          "warning",
          "No images",
          "No downloadable images were found for this story on this page.",
          undefined,
          "OK",
          "⚠️"
        );
        return;
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const objectUrl = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = objectUrl;
      const sid = sessionId ? String(sessionId).replace(/[/\\?%*:|"<>]/g, "-").slice(0, 40) : "export";
      a.download = `story-photos-${sid}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error("PhotoTraining zip download:", e);
      showModal(
        "error",
        "Download failed",
        "Could not build the ZIP of story photos. Try downloading images one at a time.",
        undefined,
        "OK",
        "❌"
      );
    } finally {
      setIsDownloadingAllPictures(false);
    }
  };

  const showModal = (
    type: 'info' | 'success' | 'warning' | 'error' | 'confirm',
    title: string,
    message: string,
    onConfirm?: () => void,
    confirmText?: string,
    icon?: string
  ) => {
    setModalState({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
      confirmText,
      icon
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const handleModalConfirm = () => {
    if (modalState.onConfirm) {
      modalState.onConfirm();
    } else {
      closeModal();
    }
  };

  return (
    <div 
      className="relative w-full min-h-screen bg-cover bg-center bg-no-repeat flex flex-col items-center px-4 pt-8"
      style={{
        backgroundImage: "url('https://pub-dd11db53b680447d86c08d43682efe32.r2.dev/images/wizard/3_background.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <main
        id="main-content"
        tabIndex={-1}
        className="relative flex w-full max-w-5xl flex-1 flex-col items-center outline-none"
      >
      {/* Error Display */}
      {(error || localError) && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 max-w-4xl w-full">
          <span className="block sm:inline">{error || localError}</span>
          <button
            onClick={() => {
              clearError();
              setLocalError(null);
            }}
            className="float-right text-red-700 hover:text-red-900"
          >
            ×
          </button>
        </div>
      )}

      {/* Main Card */}
      <div className="bg-white/95 backdrop-blur-md rounded-2xl p-8 shadow-card border border-white/40 max-w-5xl w-full z-10 mb-24">
        <h1 className="text-3xl font-extrabold text-heading mb-3 text-center">
          Photo Training 🎓
        </h1>
        <AdminPageNavigator align="center" />

        {/* Instructions */}
        <div className="bg-blue-50 rounded-lg p-6 mb-6 border-l-4 border-blue-400">
          <h2 className="text-lg font-bold text-blue-800 mb-2">📝 Instructions</h2>
          <p className="text-blue-700 text-sm">
            For each uploaded photo below, you'll see the training prompt. Copy each prompt and send it to ChatGPT manually. 
            Paste the AI's response (person description) in the text area below each image. Once all descriptions are filled, 
            click "Save Training Data" to proceed to image generation.
          </p>
        </div>

        {/* Loading State */}
        {isLoadingPrompts && (
          <div className="mb-6 p-6 bg-blue-50 rounded-lg text-center">
            <LogoLoader size="md" className="mb-4" />
            <p className="text-blue-800 font-medium">Loading training prompts...</p>
          </div>
        )}

        {/* Training Items */}
        {!isLoadingPrompts && trainingPrompts.length > 0 && (
          <>
            {isAdmin && (
              <div className="mb-6 flex justify-center">
                <button
                  type="button"
                  disabled={isDownloadingAllPictures}
                  onClick={handleDownloadAllStoryPictures}
                  className="rounded-xl bg-purple-600 px-6 py-3 text-base font-semibold text-white shadow-md transition hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isDownloadingAllPictures
                    ? "Preparing download…"
                    : "Download all pictures of this story"}
                </button>
              </div>
            )}
            <div className="space-y-6 mb-6">
            {trainingPrompts.map((promptData, index) => {
              const image = uploadedImages[index];
              // Try to get preview from WizardContext images if available
              const contextImage = images[index];
              const imageUrl =
                resolvedPhotoUrls[index] ||
                contextImage?.preview ||
                (image ? getImageUrl(image) : '');
              
              return (
                <div key={index} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <div className="flex items-start gap-6">
                    {/* Image */}
                    <div className="flex-shrink-0">
                      {image && (
                        <div className="w-48 h-48 rounded-lg overflow-hidden border-2 border-gray-300 shadow-md bg-white">
                          <img
                            src={imageUrl}
                            alt=""
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              const el = e.currentTarget;
                              console.error('PhotoTraining: image failed to load', {
                                index,
                                storageKey: image?.storageKey ?? null,
                                filename: image?.filename ?? null,
                              });
                              if (image && !contextImage?.preview) {
                                const fallbackUrl = getImageUrl(image);
                                if (fallbackUrl && fallbackUrl !== el.src) {
                                  el.src = fallbackUrl;
                                  return;
                                }
                              }
                              el.src =
                                'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
                            }}
                            onLoad={() => {
                              console.log('PhotoTraining: image loaded', {
                                index,
                                storageKey: image?.storageKey ?? null,
                              });
                            }}
                          />
                        </div>
                      )}
                      {promptData.tags && (
                        <div className="mt-2 text-center">
                          <span className="inline-block px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                            {promptData.tags}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Prompt and Description */}
                    <div className="flex-1 space-y-4">
                      {/* Prompt */}
                      <div>
                        <div className="mb-2">
                          <h3 className="font-semibold text-gray-700">Training Prompt:</h3>
                        </div>
                        <div className="mb-2">
                          <button
                            type="button"
                            onClick={() => togglePromptExpanded(index)}
                            className="w-full rounded-lg border border-blue-200 bg-white px-4 py-3 text-left hover:bg-blue-50 transition-colors"
                            aria-expanded={expandedPromptIndexes.has(index)}
                            aria-controls={`photo-training-prompt-panel-${index}`}
                          >
                            <span className="flex items-center justify-between">
                              <span>Complete Prompt</span>
                              <span>{expandedPromptIndexes.has(index) ? '▼' : '▶'}</span>
                            </span>
                          </button>
                        </div>
                        <div className="mb-2 flex flex-wrap items-center gap-3">
                          {isAdmin && (
                            <button
                              type="button"
                              disabled={!imageUrl}
                              onClick={() =>
                                handleDownloadTrainingImage(index, promptData, image, imageUrl)
                              }
                              className="px-5 py-2 rounded-lg text-base font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 bg-emerald-600 text-white hover:bg-emerald-700"
                            >
                              ⬇ Download picture
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(promptData.prompt);
                              setCopiedPromptIndexes((prev) => {
                                const next = new Set(prev);
                                next.add(index);
                                return next;
                              });
                            }}
                            className="px-5 py-2 bg-blue-600 text-white rounded-lg text-base font-semibold hover:bg-blue-700 transition"
                          >
                            <span className="flex items-center gap-2">
                              <span>📋 Copy Prompt</span>
                              {copiedPromptIndexes.has(index) && <span className="text-green-200">✅</span>}
                            </span>
                          </button>
                        </div>
                        {expandedPromptIndexes.has(index) && (
                          <div
                            id={`photo-training-prompt-panel-${index}`}
                            className="bg-white rounded-lg p-4 border border-gray-200"
                          >
                            <pre className="whitespace-pre-wrap text-sm text-gray-800 max-h-40 overflow-y-auto">
                              {promptData.prompt}
                            </pre>
                          </div>
                        )}
                      </div>

                      {/* AI Response Input */}
                      <div>
                        <label htmlFor={`photo-training-ai-response-${index}`} className="block text-sm font-medium text-gray-700 mb-2">
                          AI Response (Person Description):
                        </label>
                        <textarea
                          id={`photo-training-ai-response-${index}`}
                          value={personDescriptions[index] || ''}
                          onChange={(e) => handleDescriptionChange(index, e.target.value)}
                          onClick={() => handleTextareaClick(index)}
                          onFocus={() => handleTextareaClick(index)}
                          placeholder="Paste the AI's response here (person description)..."
                          className="w-full h-32 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {personDescriptions[index] && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Description provided ({personDescriptions[index].length} characters)
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          </>
        )}

        {/* Save Button */}
        {!isLoadingPrompts && trainingPrompts.length > 0 && (
          <div className="flex justify-center gap-4">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-brandOrange hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-8 py-3 rounded-2xl text-lg shadow-card hover:shadow-glow transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2"
            >
              {isSaving ? '💾 Saving...' : '💾 Save Training Data'}
            </button>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="absolute bottom-6 left-6 right-6 flex justify-between px-4 z-10">
        <button
          onClick={() => navigate("/create/story-studio")}
          className="bg-white/90 hover:bg-white text-heading border-2 border-brandNavy/30 font-semibold px-8 py-3 rounded-2xl text-lg shadow-soft hover:shadow-card transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2"
        >
          ← Back to Story
        </button>
      </div>
      </main>

      {/* Custom Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        onConfirm={modalState.onConfirm ? handleModalConfirm : undefined}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        confirmText={modalState.confirmText}
        icon={modalState.icon}
      />
    </div>
  );
};

export default PhotoTraining;






